import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { signOut, onAuthStateChange, getSession } from '../lib/auth';
import { getPrimaryNavItems } from '../content/navigation';
import nbnaLogo from '../assets/nbna-canada-logo.png';

export default function Header() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isProspect, setIsProspect] = useState(false);
  const [isExec, setIsExec] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Establish initial state from persisted session.
    (async () => {
      try {
        const session = await getSession();
        setIsLoggedIn(!!session);
        if (session?.user) {
          const backendRole = session.user.backendRole ?? session.user.role;
          setIsAdmin(String(session.user.role).toLowerCase() === 'admin');
          setIsExec(String(backendRole).toLowerCase() === 'executive');
          setIsProspect(String(session.user.role).toLowerCase() === 'prospect');
        }
      } catch {
        // ignore
      }
    })();

    const { data: { subscription } } = onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
      if (session?.user) {
        const backendRole = session.user.backendRole ?? session.user.role;
        setIsAdmin(String(session.user.role).toLowerCase() === 'admin');
        setIsExec(String(backendRole).toLowerCase() === 'executive');
        setIsProspect(String(session.user.role).toLowerCase() === 'prospect');
      } else {
        setIsAdmin(false);
        setIsExec(false);
        setIsProspect(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async (e) => {
    e?.preventDefault?.();
    await signOut();
    setIsLoggedIn(false);
    navigate('/login');
  };

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = getPrimaryNavItems({
    isLoggedIn,
    isAdmin,
    isExec,
    isProspect,
  });

  return (
    <header className="relative">
      {/* Hero section with image (clean, no text overlay) */}
      <div className="relative h-[20vh] overflow-hidden md:h-[400px]">
        <img
          src="https://nanaimobusinessnetworking.com/wp-content/uploads/Tech-Island-Nanaimo-hero-image@2x-min.jpg"
          alt="Business networking"
          className="absolute inset-0 h-full w-full max-h-[20vh] object-cover md:max-h-none"
        />
      </div>

      {/* Navigation */}
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            {/* Logo (left) */}
            <Link to="/" className="flex items-center">
              <img
                src={nbnaLogo}
                alt="Nanaimo Business Networking Association"
                className="h-8 w-auto"
              />
            </Link>

            {/* Desktop nav */}
            <div className="hidden sm:flex flex-1 justify-end space-x-12 py-2">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={item.kind === 'action' ? handleLogout : undefined}
                  className={`text-NBNA-primary font-playfair-sans ${location.pathname === item.to ? 'text-NBNA-primary/70' : ''}`}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Mobile hamburger (right-aligned) */}
            <div className="sm:hidden flex flex-1 justify-end">
              <button
                type="button"
                aria-label="Menu"
                className="inline-flex items-center rounded-md border px-3 py-2 text-NBNA-primary"
                onClick={() => setMobileMenuOpen((v) => !v)}
              >
                <span className="sr-only">Menu</span>
                <Bars3Icon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="sm:hidden border-t py-3 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    if (item.kind === 'action') {
                      handleLogout(e);
                    }
                  }}
                  className="block text-NBNA-primary"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}
